import { motion } from 'framer-motion';
import { FiTrash2, FiShoppingCart } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@components/common/Button';
import { useCart } from '@hooks/useCart';

/**
 * CartPage
 * Features:
 * - Display cart items
 * - Update quantity/remove items
 * - Subtotal, taxes, installation fees, total
 * - Checkout button → POST /api/cart/checkout
 * - Animations: row fade/slide
 */

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();

  const subtotal = getCartTotal();
  const installationFees = cartItems.reduce(
    (sum, item) => sum + (item.includeInstallation ? 50 * item.quantity : 0),
    0
  );
  const tax = (subtotal + installationFees) * 0.2; // 20% VAT
  const total = subtotal + installationFees + tax;

  const handleCheckout = () => {
    // In a real app, this would navigate to a checkout page
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiShoppingCart className="text-6xl text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add some products to get started!</p>
          <Button
            text="Continue Shopping"
            type="primary"
            onClick={() => navigate('/products')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        <h1 className="text-4xl font-bold mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-center space-x-6">
                  {/* Product Image */}
                  <Link to={`/products/${item.id}`} className="flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  </Link>

                  {/* Product Info */}
                  <div className="flex-1">
                    <Link
                      to={`/products/${item.id}`}
                      className="text-lg font-semibold text-gray-900 hover:text-secondary-600 transition-colors duration-300"
                    >
                      {item.title}
                    </Link>
                    
                    {item.variant && (
                      <p className="text-sm text-gray-600 mt-1">
                        Variant: {item.variant}
                      </p>
                    )}

                    {item.includeInstallation && (
                      <p className="text-sm text-secondary-600 mt-1">
                        + Professional Installation
                      </p>
                    )}

                    <p className="text-xl font-bold text-secondary-600 mt-2">
                      £{item.price.toFixed(2)}
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors duration-300"
                    >
                      -
                    </button>
                    <span className="text-lg font-semibold w-8 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors duration-300"
                    >
                      +
                    </button>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-300"
                    title="Remove from cart"
                  >
                    <FiTrash2 className="text-xl" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-bold mb-6">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>£{subtotal.toFixed(2)}</span>
                </div>

                {installationFees > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Installation</span>
                    <span>£{installationFees.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-gray-600">
                  <span>VAT (20%)</span>
                  <span>£{tax.toFixed(2)}</span>
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-secondary-600">£{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <Button
                text="Proceed to Checkout"
                type="primary"
                onClick={handleCheckout}
                className="w-full mb-4"
              />

              <Link
                to="/products"
                className="block text-center text-secondary-600 hover:text-secondary-700 transition-colors duration-300"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;

